import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  Button,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Event as EventIcon,
  CheckCircle,
  EventAvailable,
  Share,
  Google,
  Apple,
  Warning,
  History,
  CalendarMonth,
} from '@mui/icons-material';
import { EventsDialog } from '../components/eventsDialog';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '../layout/TopBar';
import { BottomNavigation } from '../layout/BottomNavigation';
import { getUserAndInvitedEvents, Event } from '../services/eventsService';
import { getBrazilianHolidays, Holiday } from '../services/holidaysService';
import { useTodoList } from '../hooks/useTodoList';

interface DayInfo {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  events: Event[];
  todos: number;
  holiday?: Holiday;
}

export const CalendarPage: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [shareMenuAnchor, setShareMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const { todos } = useTodoList();

  const loadData = async () => {
    if (!user?.uid) return;

    try {
      const userEvents = await getUserAndInvitedEvents(user.uid);
      setEvents(userEvents);

      const currentYear = new Date().getFullYear();
      const allHolidays = [
        ...getBrazilianHolidays(currentYear),
        ...getBrazilianHolidays(currentYear + 1),
      ];
      setHolidays(allHolidays);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  useEffect(() => {
    loadData();
  }, [user?.uid]);

  const handleEventClick = (event: Event) => {
    if (event.userId && user?.uid && event.userId !== user.uid) {
      navigate(`/events/${event.id}`);
      return;
    }
    setSelectedEvent(event);
    setShowEditDialog(true);
  };

  const handleEventUpdated = async () => {
    await loadData();
    setShowEditDialog(false);
    setSelectedEvent(null);
  };

  // Gerar dias do calendário
  const getDaysInMonth = (): DayInfo[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: DayInfo[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Dias do mês anterior
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      days.push(createDayInfo(date, false));
    }

    // Dias do mês atual
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push(createDayInfo(date, true));
    }

    // Dias do próximo mês
    const remainingDays = 42 - days.length; // 6 semanas * 7 dias
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push(createDayInfo(date, false));
    }

    return days;
  };

  const createDayInfo = (date: Date, isCurrentMonth: boolean): DayInfo => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    // Filtrar eventos do usuário
    const dayEvents = events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getFullYear() === date.getFullYear() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getDate() === date.getDate();
    });

    // Filtrar todos do usuário (já vem filtrado do hook, mas garantir)
    const dayTodos = todos.filter(todo => {
      if (!todo.date || todo.userId !== user?.uid) return false; // VERIFICAÇÃO EXTRA
      const todoDate = new Date(todo.date);
      todoDate.setHours(0, 0, 0, 0);
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      return todoDate.getTime() === checkDate.getTime() && !todo.completed;
    });

    const holiday = holidays.find(h => {
      return h.date.getFullYear() === date.getFullYear() &&
        h.date.getMonth() === date.getMonth() &&
        h.date.getDate() === date.getDate();
    });

    return {
      date,
      isCurrentMonth,
      isToday: compareDate.getTime() === today.getTime(),
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      events: dayEvents,
      todos: dayTodos.length,
      holiday,
    };
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleDayClick = (dayInfo: DayInfo) => {
    setSelectedDate(dayInfo.date);
  };

  const handleShareEvent = (event: Event, anchor: HTMLElement) => {
    setSelectedEvent(event);
    setShareMenuAnchor(anchor);
  };

  const handleCloseShareMenu = () => {
    setShareMenuAnchor(null);
    setSelectedEvent(null);
  };

  const handleAddToGoogleCalendar = (event: Event) => {
    const startDate = new Date(event.date);
    const [hours, minutes] = event.time.split(':');
    startDate.setHours(parseInt(hours), parseInt(minutes));

    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + 3);

    const formatGoogleDate = (date: Date): string => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title,
      details: event.description || '',
      location: event.location || '',
      dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
    });

    // No mobile, abre o app do Google Calendar
    window.location.href = `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const handleAddToAppleCalendar = (event: Event) => {
    const startDate = new Date(event.date);
    const [hours, minutes] = event.time.split(':');
    startDate.setHours(parseInt(hours), parseInt(minutes));

    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + 3);

    const formatICSDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}00`;
    };

    // Criar arquivo .ics
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Hostly//Calendar//PT',
      'BEGIN:VEVENT',
      `DTSTART:${formatICSDate(startDate)}`,
      `DTEND:${formatICSDate(endDate)}`,
      `SUMMARY:${event.title}`,
      event.description ? `DESCRIPTION:${event.description}` : '',
      event.location ? `LOCATION:${event.location}` : '',
      `DTSTAMP:${formatICSDate(new Date())}`,
      `UID:${Date.now()}@hostly.app`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].filter(line => line).join('\r\n');

    // Download do arquivo .ics
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${event.title.replace(/\s+/g, '_')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleOpenGoogleCalendar = () => {
    // Data alvo: data selecionada ou hoje
    const targetDate = selectedDate || currentDate || new Date();
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth() + 1;
    const day = targetDate.getDate();

    // URL para abrir direto na data/mês
    // Formato: https://calendar.google.com/calendar/r/month/2024/3/1
    const webUrl = `https://calendar.google.com/calendar/r/month/${year}/${month}/${day}`;

    // Tenta abrir numa nova aba (que no mobile pode chamar o app)
    window.open(webUrl, '_blank');
  };

  const handleOpenAppleCalendar = () => {
    // Data alvo
    const targetDate = selectedDate || currentDate || new Date();

    // Referência do Apple Calendar: 1 de Jan de 2001, 00:00:00 UTC
    const referenceDate = new Date('2001-01-01T00:00:00Z');

    // Calcula segundos desde a referência
    const timeInterval = (targetDate.getTime() - referenceDate.getTime()) / 1000;

    // URL Scheme: calshow:<seconds>
    const appleCalendarUrl = `calshow:${timeInterval}`;

    window.location.href = appleCalendarUrl;

    // Fallback mensagem
    setTimeout(() => {
      // Se ainda estiver na página, talvez não tenha funcionado
      // Mas não temos como saber com certeza no navegador
    }, 500);
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const days = getDaysInMonth();

  // Filtrar eventos e tarefas do dia selecionado
  const selectedDayInfo = selectedDate ? createDayInfo(selectedDate, true) : null;

  // Categorizar eventos - APENAS DO USUÁRIO
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const upcomingEvents = events
    .filter(event => {
      const eventDate = new Date(event.date);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate >= now && event.userId === user?.uid; // VERIFICAÇÃO EXTRA
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 3);

  const todayEvents = events.filter(event => {
    const eventDate = new Date(event.date);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate.getTime() === now.getTime() && event.userId === user?.uid; // VERIFICAÇÃO EXTRA
  });

  const pastEvents = events
    .filter(event => {
      const eventDate = new Date(event.date);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate < now && event.userId === user?.uid; // VERIFICAÇÃO EXTRA
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 3);

  // Tarefas de hoje e atrasadas - JÁ VEM FILTRADO DO HOOK
  const todayTodos = todos.filter(todo => {
    if (!todo.date || todo.completed || todo.userId !== user?.uid) return false; // VERIFICAÇÃO EXTRA
    const todoDate = new Date(todo.date);
    todoDate.setHours(0, 0, 0, 0);
    return todoDate.getTime() === now.getTime();
  });

  const overdueTodos = todos.filter(todo => {
    if (!todo.date || todo.completed || todo.userId !== user?.uid) return false; // VERIFICAÇÃO EXTRA
    const todoDate = new Date(todo.date);
    todoDate.setHours(0, 0, 0, 0);
    return todoDate < now;
  });


  return (
    <>
      <TopBar
        title="Calendário"
        icon={<EventIcon sx={{ fontSize: '0.875rem' }} />}
        showBackButton={true}
        showProfileButton={true}
        showSearchButton={true}
        onBackClick={() => navigate(-1)}
        onSearchClick={() => {
          const searchInput = document.querySelector('input[placeholder*="Buscar"]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
            searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
      />

      <Box sx={{
        paddingTop: '70px',
        paddingBottom: '120px',
        padding: 2,
        maxWidth: '400px',
        margin: '0 auto',
        minHeight: '100vh',
      }}>
        {/* Botões de Integração */}
        <Paper sx={{
          p: 2,
          mb: 2,
          backgroundColor: theme.colors.card,
          borderRadius: theme.radii.medium,
          boxShadow: 'none',
          border: `1px solid ${theme.colors.border}`,
        }}>
          <Typography sx={{
            fontSize: '0.75rem',
            fontWeight: 'bold',
            color: theme.colors.text.primary,
            mb: 1.5,
          }}>
            ABRIR CALENDÁRIO:
          </Typography>
          <Box sx={{
            display: 'flex',
            gap: 1,
          }}>
            <Button
              variant="contained"
              startIcon={<Google />}
              onClick={handleOpenGoogleCalendar}
              sx={{
                flex: 1,
                backgroundColor: '#4285F4',
                color: '#fff',
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#357ae8',
                },
              }}
            >
              Google
            </Button>
            <Button
              variant="contained"
              startIcon={<Apple />}
              onClick={handleOpenAppleCalendar}
              sx={{
                flex: 1,
                backgroundColor: '#000',
                color: '#fff',
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#333',
                },
              }}
            >
              Apple
            </Button>
          </Box>
        </Paper>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 4 }}>
          {/* Coluna Principal: Calendário */}
          <Box>

            {/* Header do Calendário */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}>
              <IconButton
                onClick={() => navigateMonth('prev')}
                sx={{
                  backgroundColor: theme.colors.primary,
                  color: theme.colors.text.contrast,
                  width: 32,
                  height: 32,
                  '&:hover': { backgroundColor: theme.colors.primary, opacity: 0.8 },
                }}
              >
                <ChevronLeft />
              </IconButton>

              <Typography sx={{
                fontSize: '1rem',
                fontWeight: 'bold',
                color: theme.colors.text.primary,
              }}>
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </Typography>

              <IconButton
                onClick={() => navigateMonth('next')}
                sx={{
                  backgroundColor: theme.colors.primary,
                  color: theme.colors.text.contrast,
                  width: 32,
                  height: 32,
                  '&:hover': { backgroundColor: theme.colors.primary, opacity: 0.8 },
                }}
              >
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Dias da Semana */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0.5,
              mb: 1,
            }}>
              {weekDays.map((day, index) => (
                <Box key={day} sx={{ textAlign: 'center' }}>
                  <Typography sx={{
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    color: index === 0 ? theme.colors.secondary : theme.colors.text.primary,
                  }}>
                    {day}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Grid do Calendário */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0.5,
              mb: 3,
            }}>
              {days.map((dayInfo, index) => (
                <Box
                  key={index}
                  onClick={() => dayInfo.isCurrentMonth && handleDayClick(dayInfo)}
                  sx={{
                    aspectRatio: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    backgroundColor: dayInfo.isToday
                      ? theme.colors.secondary
                      : dayInfo.isCurrentMonth
                        ? theme.colors.card
                        : theme.colors.background,
                    borderRadius: theme.radii.small,
                    cursor: dayInfo.isCurrentMonth ? 'pointer' : 'default',
                    opacity: dayInfo.isCurrentMonth ? 1 : 0.3,
                    border: dayInfo.holiday ? `1px solid ${theme.colors.secondary}` : 'none',
                    '&:hover': dayInfo.isCurrentMonth ? {
                      backgroundColor: dayInfo.isToday ? theme.colors.secondary : theme.colors.primary,
                      opacity: 0.8,
                    } : {},
                  }}
                >
                  <Typography sx={{
                    fontSize: '0.75rem',
                    fontWeight: dayInfo.isToday ? 'bold' : 'normal',
                    color: dayInfo.isToday
                      ? theme.colors.text.contrast
                      : dayInfo.isWeekend
                        ? theme.colors.secondary
                        : theme.colors.text.primary,
                  }}>
                    {dayInfo.date.getDate()}
                  </Typography>

                  {/* Indicadores */}
                  <Box sx={{
                    display: 'flex',
                    gap: 0.25,
                    position: 'absolute',
                    bottom: 2,
                  }}>
                    {dayInfo.events.length > 0 && (
                      <Badge
                        badgeContent={dayInfo.events.length}
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.text.contrast,
                            fontSize: '0.5rem',
                            minWidth: 12,
                            height: 12,
                            padding: 0,
                          },
                        }}
                      />
                    )}
                    {dayInfo.todos > 0 && (
                      <Badge
                        badgeContent={dayInfo.todos}
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: '#FFA500',
                            color: 'white',
                            fontSize: '0.5rem',
                            minWidth: 12,
                            height: 12,
                            padding: 0,
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box>

            {/* Resumo Rápido - NOVO */}
            {!selectedDate && (
              <Box sx={{ mb: 3 }}>
                {/* Eventos de Hoje */}
                {todayEvents.length > 0 && (
                  <Paper sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: theme.colors.card,
                    borderRadius: theme.radii.medium,
                    boxShadow: 'none',
                    border: `1px solid ${theme.colors.border}`,
                  }}>
                    <Typography sx={{
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: theme.colors.primary,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      <EventAvailable fontSize="small" />
                      HOJE ({todayEvents.length})
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      {todayEvents.map((event) => (
                        <ListItem
                          key={event.id}
                          disablePadding
                          sx={{
                            backgroundColor: theme.colors.background,
                            borderRadius: theme.radii.small,
                            mb: 0.5,
                          }}
                        >
                          <ListItemButton
                            onClick={() => handleEventClick(event)}
                            sx={{ p: 1, borderRadius: theme.radii.small }}
                          >
                            <EventAvailable sx={{ mr: 1, fontSize: '1rem', color: theme.colors.primary }} />
                            <ListItemText
                              primary={event.title}
                              secondary={event.time}
                              slotProps={{
                                primary: { sx: { fontSize: '0.8rem', fontWeight: 'bold' } },
                                secondary: { sx: { fontSize: '0.7rem' } }
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}

                {/* Tarefas de Hoje */}
                {todayTodos.length > 0 && (
                  <Paper sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: theme.colors.card,
                    borderRadius: theme.radii.medium,
                    boxShadow: 'none',
                    border: `1px solid ${theme.colors.border}`,
                  }}>
                    <Typography sx={{
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: '#FFA500',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      <CheckCircle fontSize="small" />
                      TAREFAS HOJE ({todayTodos.length})
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      {todayTodos.slice(0, 3).map((todo) => (
                        <ListItem
                          key={todo.id}
                          sx={{
                            backgroundColor: theme.colors.background,
                            borderRadius: theme.radii.small,
                            mb: 0.5,
                            p: 1,
                          }}
                        >
                          <CheckCircle sx={{ mr: 1, fontSize: '1rem', color: '#FFA500' }} />
                          <ListItemText
                            primary={todo.title}
                            slotProps={{
                              primary: { sx: { fontSize: '0.8rem' } }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    {todayTodos.length > 3 && (
                      <Typography sx={{
                        fontSize: '0.7rem',
                        color: theme.colors.text.secondary,
                        textAlign: 'center',
                        mt: 1,
                      }}>
                        +{todayTodos.length - 3} tarefas
                      </Typography>
                    )}
                  </Paper>
                )}

                {/* Tarefas Atrasadas */}
                {overdueTodos.length > 0 && (
                  <Paper sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: '#FFEBEE',
                    borderRadius: theme.radii.medium,
                    boxShadow: 'none',
                    border: '1px solid #EF5350',
                  }}>
                    <Typography sx={{
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: '#D32F2F',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      <Warning fontSize="small" />
                      ATRASADAS ({overdueTodos.length})
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      {overdueTodos.slice(0, 3).map((todo) => (
                        <ListItem
                          key={todo.id}
                          sx={{
                            backgroundColor: '#fff',
                            borderRadius: theme.radii.small,
                            mb: 0.5,
                            p: 1,
                          }}
                        >
                          <CheckCircle sx={{ mr: 1, fontSize: '1rem', color: '#EF5350' }} />
                          <ListItemText
                            primary={todo.title}
                            secondary={`Venceu em ${new Date(todo.date).toLocaleDateString('pt-BR')}`}
                            slotProps={{
                              primary: { sx: { fontSize: '0.8rem', color: '#D32F2F' } },
                              secondary: { sx: { fontSize: '0.7rem', color: '#EF5350' } }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    {overdueTodos.length > 3 && (
                      <Typography sx={{
                        fontSize: '0.7rem',
                        color: '#D32F2F',
                        textAlign: 'center',
                        mt: 1,
                      }}>
                        +{overdueTodos.length - 3} tarefas atrasadas
                      </Typography>
                    )}
                  </Paper>
                )}

                {/* Próximos Eventos */}
                {upcomingEvents.length > 0 && (
                  <Paper sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: theme.colors.card,
                    borderRadius: theme.radii.medium,
                    boxShadow: 'none',
                    border: `1px solid ${theme.colors.border}`,
                  }}>
                    <Typography sx={{
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: theme.colors.text.primary,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      <CalendarMonth fontSize="small" />
                      PRÓXIMOS EVENTOS
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      {upcomingEvents.map((event) => (
                        <ListItem
                          key={event.id}
                          disablePadding
                          sx={{
                            backgroundColor: theme.colors.background,
                            borderRadius: theme.radii.small,
                            mb: 0.5,
                          }}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={(e: React.MouseEvent<HTMLButtonElement>) => event.id && handleShareEvent(event, e.currentTarget)}
                            >
                              <Share fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemButton
                            onClick={() => handleEventClick(event)}
                            sx={{ p: 1, pr: 5, borderRadius: theme.radii.small }}
                          >
                            <EventAvailable sx={{ mr: 1, fontSize: '1rem', color: theme.colors.primary }} />
                            <ListItemText
                              primary={event.title}
                              secondary={`${new Date(event.date).toLocaleDateString('pt-BR')} às ${event.time}`}
                              slotProps={{
                                primary: { sx: { fontSize: '0.8rem', fontWeight: 'bold' } },
                                secondary: { sx: { fontSize: '0.7rem' } }
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}

                {/* Eventos Passados */}
                {pastEvents.length > 0 && (
                  <Paper sx={{
                    p: 2,
                    backgroundColor: theme.colors.card,
                    borderRadius: theme.radii.medium,
                    boxShadow: 'none',
                    border: `1px solid ${theme.colors.border}`,
                  }}>
                    <Typography sx={{
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: theme.colors.text.secondary,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      <History fontSize="small" />
                      EVENTOS ANTERIORES
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      {pastEvents.map((event) => (
                        <ListItem
                          key={event.id}
                          disablePadding
                          sx={{
                            backgroundColor: theme.colors.background,
                            borderRadius: theme.radii.small,
                            mb: 0.5,
                            opacity: 0.7,
                          }}
                        >
                          <ListItemButton
                            onClick={() => handleEventClick(event)}
                            sx={{ p: 1, borderRadius: theme.radii.small }}
                          >
                            <EventAvailable sx={{ mr: 1, fontSize: '1rem', color: theme.colors.text.secondary }} />
                            <ListItemText
                              primary={event.title}
                              secondary={new Date(event.date).toLocaleDateString('pt-BR')}
                              slotProps={{
                                primary: { sx: { fontSize: '0.8rem' } },
                                secondary: { sx: { fontSize: '0.7rem' } }
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                    {pastEvents.length > 3 && (
                      <Typography sx={{
                        fontSize: '0.7rem',
                        color: theme.colors.text.secondary,
                        textAlign: 'center',
                        mt: 1,
                      }}>
                        Ver todos os eventos passados
                      </Typography>
                    )}
                  </Paper>
                )}
              </Box>
            )}

            {/* Detalhes do Dia Selecionado */}
            {selectedDayInfo && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1,
                }}>
                  <Typography sx={{
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    color: theme.colors.text.primary,
                    flex: 1,
                  }}>
                    {selectedDayInfo.date.toLocaleDateString('pt-BR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </Typography>

                  {(selectedDayInfo.events.length > 0 || selectedDayInfo.todos > 0) && (
                    <IconButton
                      size="small"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        const allEvents = selectedDayInfo.events;
                        if (allEvents.length > 0) {
                          setSelectedEvent(allEvents[0]);
                          setShareMenuAnchor(e.currentTarget);
                        }
                      }}
                      sx={{
                        backgroundColor: theme.colors.primary,
                        color: theme.colors.text.contrast,
                        width: 28,
                        height: 28,
                        '&:hover': {
                          backgroundColor: theme.colors.primary,
                          opacity: 0.8,
                        },
                      }}
                    >
                      <Share sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  )}
                </Box>

                {selectedDayInfo.holiday && (
                  <Chip
                    label={selectedDayInfo.holiday.name}
                    size="small"
                    sx={{
                      backgroundColor: theme.colors.secondary,
                      color: theme.colors.text.onSecondary,
                      mb: 1,
                    }}
                  />
                )}

                {/* Eventos do Dia */}
                {selectedDayInfo.events.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: theme.colors.text.primary,
                      mb: 1,
                    }}>
                      Eventos
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      {selectedDayInfo.events.map((event) => (
                        <ListItem
                          key={event.id}
                          disablePadding
                          sx={{
                            backgroundColor: theme.colors.card,
                            borderRadius: theme.radii.small,
                            mb: 0.5,
                          }}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={(e: React.MouseEvent<HTMLButtonElement>) => event.id && handleShareEvent(event, e.currentTarget)}
                            >
                              <Share fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemButton
                            onClick={() => handleEventClick(event)}
                            sx={{ p: 1, pr: 5, borderRadius: theme.radii.small }}
                          >
                            <EventAvailable sx={{ mr: 1, fontSize: '1rem', color: theme.colors.primary }} />
                            <ListItemText
                              primary={event.title}
                              secondary={event.time}
                              slotProps={{
                                primary: { sx: { fontSize: '0.8rem', fontWeight: 'bold' } },
                                secondary: { sx: { fontSize: '0.7rem' } }
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Tarefas do Dia */}
                {selectedDayInfo.todos > 0 && (
                  <Box>
                    <Typography sx={{
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: theme.colors.text.primary,
                      mb: 1,
                    }}>
                      Tarefas
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      {todos
                        .filter(todo => {
                          if (!todo.date) return false;
                          const todoDate = new Date(todo.date);
                          todoDate.setHours(0, 0, 0, 0);
                          const selectedDateNormalized = new Date(selectedDayInfo.date);
                          selectedDateNormalized.setHours(0, 0, 0, 0);
                          return todoDate.getTime() === selectedDateNormalized.getTime() &&
                            !todo.completed;
                        })
                        .map((todo) => (
                          <ListItem
                            key={todo.id}
                            sx={{
                              backgroundColor: theme.colors.card,
                              borderRadius: theme.radii.small,
                              mb: 0.5,
                              p: 1,
                            }}
                          >
                            <CheckCircle sx={{ mr: 1, fontSize: '1rem', color: '#FFA500' }} />
                            <ListItemText
                              primary={todo.title}
                              slotProps={{
                                primary: { sx: { fontSize: '0.8rem' } }
                              }}
                            />
                          </ListItem>
                        ))}
                    </List>
                  </Box>
                )}

                {selectedDayInfo.events.length === 0 && selectedDayInfo.todos === 0 && !selectedDayInfo.holiday && (
                  <Typography sx={{
                    fontSize: '0.75rem',
                    color: theme.colors.text.secondary,
                    textAlign: 'center',
                    py: 2,
                  }}>
                    Nenhum evento ou tarefa para este dia
                  </Typography>
                )}
              </Box>
            )}

          </Box>
        </Box>

        {/* Menu de Compartilhamento */}
        <Menu
          anchorEl={shareMenuAnchor}
          open={Boolean(shareMenuAnchor)}
          onClose={handleCloseShareMenu}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: theme.colors.card,
                borderRadius: theme.radii.medium,
                boxShadow: theme.shadows.medium,
                border: `1px solid ${theme.colors.border}`,
              }
            }
          }}
        >
          <MenuItem
            onClick={() => {
              if (selectedEvent) {
                handleAddToGoogleCalendar(selectedEvent);
                handleCloseShareMenu();
              }
            }}
            sx={{
              fontSize: '0.85rem',
              color: theme.colors.text.primary,
              '&:hover': {
                backgroundColor: theme.colors.background,
              }
            }}
          >
            <Google sx={{ mr: 1.5, fontSize: '1.2rem', color: '#4285F4' }} />
            Adicionar ao Google
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (selectedEvent) {
                handleAddToAppleCalendar(selectedEvent);
                handleCloseShareMenu();
              }
            }}
            sx={{
              fontSize: '0.85rem',
              color: theme.colors.text.primary,
              '&:hover': {
                backgroundColor: theme.colors.background,
              }
            }}
          >
            <Apple sx={{ mr: 1.5, fontSize: '1.2rem' }} />
            Baixar (.ics)
          </MenuItem>
        </Menu>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <BottomNavigation />
      </Box>

      {/* Dialog de Edição do Evento */}
      {selectedEvent && (
        <EventsDialog
          open={showEditDialog}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedEvent(null);
          }}
          eventData={{
            id: selectedEvent.id,
            title: selectedEvent.title,
            date: selectedEvent.date instanceof Date
              ? selectedEvent.date.toISOString().split('T')[0]
              : selectedEvent.date,
            time: selectedEvent.time,
            location: selectedEvent.location,
            description: selectedEvent.description,
            guests: selectedEvent.guests,
            recipes: selectedEvent.recipes,
            inventoryItems: selectedEvent.inventoryItems,
            tasks: selectedEvent.tasks,
            outfit: selectedEvent.outfit,
            guestCount: selectedEvent.guestCount,
          }}
          onEventCreated={handleEventUpdated}
        />
      )}
    </>
  );
};
